import { simulateExitPlacement } from "../services/reportsApi";
import {
  fetchReportPolicySimConfig,
  saveSimulateTradePolicyJob,
  upsertReportPolicySimConfig,
  waitForSimulateTradePolicyJob,
} from "../services/tradePolicyApi";
import { parseSimulateBacktestMeta, patchBacktest } from "../services/backtestsApi";
import type {
  PolicyTreeV2,
  TradePolicySimulateResponse,
} from "../types/tradePolicy";
import {
  extractSimulatePolicyTreeV2,
  simulatePolicySchemaVersion,
} from "../types/tradePolicy";
import type { SimulateExitPlacementResponse, SimulateLegs, SimulatedLeg, ExitPlacementSliceRef } from "../types/managementExplorer";
import type { WorkspaceNodeMeta, WorkspaceNodePosition } from "../types/workspace";
import { sanitizeWorkspaceSimulateAttachFields } from "./workspacePersistIds";
import { exitPlacementSlicesDefaultTitle } from "./exitPlacementDisplay";
import { prepareExitPlacementPolicyForSimulate } from "./exitPlacementPolicy";
import { defaultSimulateLegs, normalizeSimulatedLegs, normalizeSimulateLegs } from "./exitPlacementSimLegs";

export type SimulateExitPlacementSlicesParams = {
  reportId: string;
  sliceIds: string[];
  /** Edited policy_tree.v2 — sent when user adjusts SL/TP ATR in the policy tree. */
  policy?: PolicyTreeV2;
  /** Run title from the simulator (persisted on the saved backtest). */
  title?: string;
  symbol?: string | null;
  simConfigRevision?: number;
  /** Which SL/TP legs to replay (default sl_only when both exist). */
  simulateLegs?: SimulateLegs;
  /** Canvas attach: report node as parent when saving the backtest. */
  workspaceId?: string;
  parentNodeId?: string;
  position?: WorkspaceNodePosition;
  meta?: WorkspaceNodeMeta;
};

export type SimulateExitPlacementSlicesResult = {
  jobId: string;
  backtestId?: string;
  result: TradePolicySimulateResponse;
  policy: PolicyTreeV2;
  simulateLegs?: SimulateLegs;
  simulatedLegs?: SimulatedLeg[];
  exitPlacementSlices?: ExitPlacementSliceRef[];
};

export type ExitPlacementSimulationStart = {
  jobId: string;
  backtestId?: string;
  policy: PolicyTreeV2;
  policySchemaVersion: string;
  result: TradePolicySimulateResponse;
  needsPoll: boolean;
  simulateLegs: SimulateLegs;
  simulatedLegs?: SimulatedLeg[];
  exitPlacementSlices?: ExitPlacementSliceRef[];
  editedPolicy?: PolicyTreeV2;
  raw: SimulateExitPlacementResponse;
};

export function exitPlacementSimMetricsReady(result: TradePolicySimulateResponse): boolean {
  const baselineCount = result.baseline?.trade_count;
  const filteredCount = result.filtered?.trade_count;
  return (
    (typeof baselineCount === "number" && Number.isFinite(baselineCount)) ||
    (typeof filteredCount === "number" && Number.isFinite(filteredCount))
  );
}

export function normalizeExitPlacementSimResponse(
  raw: unknown,
  httpStatus?: number
): {
  policy: PolicyTreeV2 | null;
  policySchemaVersion: string;
  jobId: string;
  backtestId?: string;
  result: TradePolicySimulateResponse;
  needsPoll: boolean;
  simulateLegs?: SimulateLegs;
  simulatedLegs?: SimulatedLeg[];
  exitPlacementSlices?: ExitPlacementSliceRef[];
} {
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid exit placement simulation response.");
  }
  const body = raw as SimulateExitPlacementResponse;

  const jobId = String(body.job_id ?? "").trim();
  if (!jobId) {
    throw new Error("Simulation did not return a job id.");
  }

  const policy = extractSimulatePolicyTreeV2(body);
  if (!policy && httpStatus !== 202) {
    throw new Error("Simulation did not return a policy_tree.v2 payload.");
  }

  const policySchemaVersion =
    simulatePolicySchemaVersion(body) ??
    policy?.schema_version ??
    "policy_tree.v2";

  const backtestId = parseSimulateBacktestMeta(body).backtestId;

  const result: TradePolicySimulateResponse = {
    baseline: body.baseline,
    filtered: body.filtered,
    ...(body.removed ? { removed: body.removed } : {}),
    ...(body.changed ? { changed: body.changed } : {}),
    delta: body.delta as TradePolicySimulateResponse["delta"],
    ...(body.meta ? { meta: body.meta as TradePolicySimulateResponse["meta"] } : {}),
    job_id: jobId,
    simulated_trades: body.simulated_trades,
    baseline_trades: body.baseline_trades,
    kept_trade_ids: body.kept_trade_ids,
  };

  const status = String(body.status ?? "").trim().toLowerCase();
  const needsPoll =
    httpStatus === 202 ||
    status === "accepted" ||
    status === "processing" ||
    status === "queued" ||
    status === "running" ||
    status === "pending" ||
    !exitPlacementSimMetricsReady(result);

  return {
    policy,
    policySchemaVersion,
    jobId,
    backtestId,
    result,
    needsPoll,
    simulateLegs: normalizeSimulateLegs(body.simulate_legs),
    simulatedLegs: normalizeSimulatedLegs(body.simulated_legs),
    exitPlacementSlices: Array.isArray(body.exit_placement_slices)
      ? body.exit_placement_slices
      : undefined,
  };
}

/**
 * Enqueue Exit Placement simulation (POST only). Poll and persist via
 * {@link completeExitPlacementSimulation} when `needsPoll` is true.
 */
export async function startExitPlacementSimulation(
  params: SimulateExitPlacementSlicesParams
): Promise<ExitPlacementSimulationStart> {
  const sliceIds = params.sliceIds.map((id) => id.trim()).filter(Boolean);
  if (sliceIds.length === 0) {
    throw new Error("Select at least one explorer slice to simulate.");
  }

  const simulateLegs = params.simulateLegs ?? defaultSimulateLegs({ hasSl: true, hasTp: true });

  const editedPolicy = params.policy
    ? prepareExitPlacementPolicyForSimulate(params.policy, simulateLegs)
    : undefined;
  const editedPolicySchemaVersion = editedPolicy
    ? simulatePolicySchemaVersion({
        policy: editedPolicy,
        policy_schema_version: editedPolicy.schema_version,
      }) ?? editedPolicy.schema_version ?? "policy_tree.v2"
    : undefined;

  const attach = sanitizeWorkspaceSimulateAttachFields({
    workspaceId: params.workspaceId,
    parentNodeId: params.parentNodeId,
    position: params.position,
    meta: params.meta,
  });
  const hasWorkspaceAttach = Boolean(attach.workspaceId && attach.parentNodeId);
  // Always ask EP to sync-persist. completeExitPlacementSimulation only POSTs
  // /simulate/jobs/{id}/save when the sync/poll payload still has no backtest_id
  // (avoids double-persist when BE already saved on simulate-exit-placement).
  const saveBacktest = true;

  const rawResponse = await simulateExitPlacement(params.reportId, {
    slice_ids: sliceIds,
    ...(editedPolicy
      ? {
          policy: editedPolicy,
          policy_tree: editedPolicy,
          simulation_policy: editedPolicy,
          policy_schema_version: editedPolicySchemaVersion,
        }
      : {}),
    ...(params.title?.trim() ? { title: params.title.trim() } : {}),
    simulate_legs: simulateLegs,
    save_backtest: saveBacktest,
    include_trade_ids: true,
    simulate_ohlc_fill_missing: false,
    ...(hasWorkspaceAttach
      ? {
          workspaceId: attach.workspaceId,
          parentNodeId: attach.parentNodeId,
          ...(attach.position ? { position: attach.position } : {}),
        }
      : {}),
  });
  const raw = rawResponse.body as SimulateExitPlacementResponse;

  const normalized = normalizeExitPlacementSimResponse(raw, rawResponse.status);

  let policy = editedPolicy ?? normalized.policy;
  if (!policy) {
    throw new Error("Simulation did not return a policy_tree.v2 payload.");
  }

  let policySchemaVersion = normalized.policySchemaVersion;
  if (editedPolicySchemaVersion) {
    policySchemaVersion = editedPolicySchemaVersion;
  }

  return {
    jobId: normalized.jobId,
    backtestId: normalized.backtestId,
    policy,
    policySchemaVersion,
    result: normalized.result,
    needsPoll: normalized.needsPoll,
    simulateLegs,
    simulatedLegs: normalized.simulatedLegs,
    exitPlacementSlices: normalized.exitPlacementSlices,
    editedPolicy,
    raw,
  };
}

/** Poll (when needed), save backtest, and link policy-sim-config after {@link startExitPlacementSimulation}. */
export async function completeExitPlacementSimulation(
  params: SimulateExitPlacementSlicesParams,
  start: ExitPlacementSimulationStart
): Promise<SimulateExitPlacementSlicesResult> {
  const sliceIds = params.sliceIds.map((id) => id.trim()).filter(Boolean);
  const simulateLegs = start.simulateLegs;
  const editedPolicy = start.editedPolicy;

  let policy = start.policy;
  let policySchemaVersion = start.policySchemaVersion;
  let jobId = start.jobId;
  let backtestId =
    start.backtestId?.trim() || parseSimulateBacktestMeta(start.raw).backtestId;
  let result = start.result;
  let simulatedLegs = start.simulatedLegs;
  let exitPlacementSlices = start.exitPlacementSlices;
  const raw = start.raw;

  if (start.needsPoll) {
    const outcome = await waitForSimulateTradePolicyJob(jobId);
    result = {
      ...result,
      ...outcome.result,
      job_id: jobId,
    };
    backtestId = backtestId ?? outcome.backtestId?.trim() ?? undefined;
    if (!editedPolicy && outcome.policyTree) {
      policy = outcome.policyTree;
    }
    if (outcome.policySchemaVersion) {
      policySchemaVersion = outcome.policySchemaVersion;
    }
  }

  const policyForPersist = editedPolicy ?? policy;

  // Only /save when sync + poll never returned a backtest_id (BE 202 guidance).
  if (!backtestId) {
    const attach = sanitizeWorkspaceSimulateAttachFields({
      workspaceId: params.workspaceId,
      parentNodeId: params.parentNodeId,
      position: params.position,
      meta: params.meta,
    });
    const saveOverrides = {
      ...(editedPolicy
        ? { policy_tree: policyForPersist, simulate_legs: simulateLegs }
        : {}),
      ...(attach.workspaceId ? { workspaceId: attach.workspaceId } : {}),
      ...(attach.parentNodeId ? { parentNodeId: attach.parentNodeId } : {}),
      ...(attach.position ? { position: attach.position } : {}),
      ...(attach.meta ? { meta: attach.meta } : {}),
    };
    const saved = await saveSimulateTradePolicyJob(
      jobId,
      Object.keys(saveOverrides).length > 0 ? saveOverrides : undefined
    );
    if (!saved.ok) {
      throw new Error(saved.detail || "Simulation finished but could not save backtest.");
    }
    backtestId = saved.backtestId;
  }

  if (!backtestId) {
    throw new Error("Simulation did not persist a backtest.");
  }

  const runTitle =
    params.title?.trim() ||
    exitPlacementSlicesDefaultTitle(
      Array.isArray(raw.exit_placement_slices) ? raw.exit_placement_slices : undefined
    ) ||
    undefined;
  if (runTitle) {
    try {
      await patchBacktest(String(backtestId), { title: runTitle });
    } catch {
      // UI still shows the in-session title; merge logic preserves it over hydrated defaults.
    }
  }

  const revision =
    typeof params.simConfigRevision === "number"
      ? params.simConfigRevision
      : (await fetchReportPolicySimConfig(params.reportId))?.revision ?? 0;

  const ruleCount = Array.isArray(policy.rules) ? policy.rules.length : sliceIds.length;

  await upsertReportPolicySimConfig(params.reportId, {
    policy_tree: policyForPersist,
    policy_schema_version: policySchemaVersion,
    linked_backtest_id: String(backtestId),
    linked_job_id: jobId,
    simulation_overlay_mode: "baseline_vs_simulated",
    scope_inputs: {
      symbol: params.symbol?.trim() ?? "",
      slice_ids: sliceIds,
      simulate_legs: simulateLegs,
    },
    revision,
    group_count: ruleCount,
    rule_count: ruleCount,
  });

  return {
    jobId,
    backtestId: String(backtestId),
    result,
    policy: policyForPersist,
    simulateLegs,
    simulatedLegs,
    exitPlacementSlices,
  };
}

/**
 * Run Exit Placement simulation via the report-scoped endpoint (blocking).
 * Prefer start + background complete for UI flows that should stay interactive.
 */
export async function simulateExitPlacementSlices(
  params: SimulateExitPlacementSlicesParams
): Promise<SimulateExitPlacementSlicesResult> {
  const start = await startExitPlacementSimulation(params);
  return completeExitPlacementSimulation(params, start);
}

/** @deprecated Use {@link simulateExitPlacementSlices}. */
export async function simulateManagementCandidate(
  params: SimulateExitPlacementSlicesParams
): Promise<SimulateExitPlacementSlicesResult> {
  return simulateExitPlacementSlices(params);
}
